import React from 'react';
import { keyframes, styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { HelpPanelContainerV2 } from '../styled.tsx';

type HelpPanelLoadingProps = {
	loadingArticle?: boolean;
};

const HelpPanelLoading = ({ loadingArticle = false }: HelpPanelLoadingProps) =>
	loadingArticle ? (
		<HelpPanelContainerV2>
			<HeaderLoading />
			<LoadingContent>
				<div role="img" aria-label="help panel loading">
					<LoadingRectangle contentHeight={token('space.250')} marginTop={token('space.0')} />
					<LoadingRectangle contentWidth="90%" />
					<LoadingRectangle contentWidth="80%" />
					<LoadingRectangle contentWidth="80%" />
					<LoadingRectangle contentWidth="80%" />
					<LoadingRectangle contentWidth="80%" />
					<LoadingRectangle contentWidth="70%" />
				</div>
			</LoadingContent>
		</HelpPanelContainerV2>
	) : (
		<HelpPanelContainerV2>
			<HeaderLoading />
			<LoadingContent>
				<LoadingRectangle contentHeight={token('space.500')} marginTop={token('space.100')} />
				<LoadignArticleList role="img">
					<LoadignArticleListItem>
						<LoadingRectangle
							contentHeight={token('space.150')}
							contentWidth={token('space.800')}
							marginTop={token('space.0')}
						/>
						<LoadingRectangle contentWidth="100%" marginTop={token('space.050')} />
						<LoadingRectangle contentWidth="100%" marginTop={token('space.150')} />
						<LoadingRectangle contentWidth="100%" marginTop={token('space.050')} />
						<LoadingRectangle contentWidth="100%" marginTop={token('space.050')} />
					</LoadignArticleListItem>

					<LoadignArticleListItem>
						<LoadingRectangle
							contentHeight={token('space.150')}
							contentWidth={token('space.800')}
							marginTop={token('space.0')}
						/>
						<LoadingRectangle contentWidth="100%" marginTop={token('space.050')} />
						<LoadingRectangle contentWidth="100%" marginTop={token('space.150')} />
						<LoadingRectangle contentWidth="100%" marginTop={token('space.050')} />
						<LoadingRectangle contentWidth="100%" marginTop={token('space.050')} />
					</LoadignArticleListItem>

					<LoadignArticleListItem>
						<LoadingRectangle
							contentHeight={token('space.150')}
							contentWidth={token('space.800')}
							marginTop={token('space.0')}
						/>
						<LoadingRectangle contentWidth="100%" marginTop={token('space.050')} />
						<LoadingRectangle contentWidth="100%" marginTop={token('space.150')} />
						<LoadingRectangle contentWidth="100%" marginTop={token('space.050')} />
						<LoadingRectangle contentWidth="100%" marginTop={token('space.050')} />
					</LoadignArticleListItem>
				</LoadignArticleList>
			</LoadingContent>
		</HelpPanelContainerV2>
	);

export default HelpPanelLoading;

/**
 * Header
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingContent = styled.div({
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderLoading = styled.div({
	height: token('space.800'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.selected'),
	borderBottom: `${token('space.025')} solid ${token('color.background.selected')}`,
	justifyContent: 'space-between',
	position: 'relative',
});

/**
 * Loading Rectangle
 */

type LoadingRectangleProps = {
	contentHeight?: string | number;
	marginTop?: string | number;
	contentWidth?: string | number;
};

const shimmer = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '1000px 0',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingRectangle = styled.div<LoadingRectangleProps>({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => (props.contentHeight != null ? props.contentHeight : token('space.200')),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: (props) => (props.marginTop != null ? props.marginTop : token('space.100')),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: (props) => (props.contentWidth != null ? props.contentWidth : '100%'),
	borderRadius: token('space.025'),
	animationDuration: '1.2s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationName: shimmer,
	animationTimingFunction: 'linear',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.gray.subtler'),
	backgroundImage: `linear-gradient( to right, ${token('color.background.accent.gray.subtler')} 10%, ${token('color.background.accent.gray.subtle')} 20%, ${token('color.background.accent.gray.subtler')} 30% )`,
	backgroundRepeat: 'no-repeat',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadignArticleList = styled.div({
	width: '100%',
	marginTop: token('space.100'),
	marginRight: 0,
	marginBottom: 0,
	marginLeft: 0,
	paddingTop: token('space.0'),
	paddingRight: token('space.0'),
	paddingBottom: token('space.0'),
	paddingLeft: token('space.0'),
	boxSizing: 'border-box',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadignArticleListItem = styled.div({
	display: 'block',
	width: '100%',
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	boxSizing: 'border-box',
});
